// frontend/src/admin/v2/hooks/useAudit.js
import { useEffect, useState, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_BASE_URL
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY
const HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}

/**
 * useAudit — fetches /api/admin/audit with filter state.
 *
 * @param filters {sources: string[], statuses: string[], hours: number, q: string, limit: number, offset: number}
 *   sources / statuses are arrays — joined to comma strings for the query.
 *
 * AbortController cancels inflight on filter change.
 *
 * Returns { data, loading, error, errorTitle, refetch }.
 *
 * 🔴 Welle 4 PR 1 (§4.1): `error` ist der Nutzersatz, `errorTitle` traegt das
 * Entwicklerdetail (den Env-Namen) fuer den Tooltip.
 */
export function useAudit(filters) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorTitle, setErrorTitle] = useState(null)
  const inflightRef = useRef(null)

  // Stringify filters so the effect re-runs on shape change without
  // depending on object identity.
  const fingerprint = JSON.stringify({
    sources: filters.sources || [],
    statuses: filters.statuses || [],
    hours: filters.hours || 24,
    q: filters.q || '',
    limit: filters.limit || 200,
    offset: filters.offset || 0,
  })

  const fetchOnce = useCallback(async () => {
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c

    // 🔴 R15 W2 (G14a-3 NB3): `loading` gehoert an den ANFANG jedes Abrufs, nicht
    // nur in die Initialisierung. Bis hierher stand `setLoading(true)` nur im
    // `useState`-Startwert und `false` im `finally` — nach dem ersten Aufbau war
    // `loading` deshalb konstant `false`, und die zwei Props, die daran haengen,
    // waren tot: `aria-busy` am Zeilenkasten (`AuditFeed.jsx`) und der
    // Ladezustand des Zaehlers (`ResultCount`). Die Registry sagt fuer diesen
    // Weg ausdruecklich zu, dass beim Neuladen `aria-busy` dazukommt.
    // `useAudit` hat genau EINEN Aufrufer (`AuditFeed`), das Geruest haengt
    // weiter an `!jeGeladen` — ein gesetztes `loading` laesst es also stehen.
    setLoading(true)

    const f = JSON.parse(fingerprint)
    const params = {
      hours: f.hours,
      limit: f.limit,
      offset: f.offset,
    }
    if (f.sources.length) params.source = f.sources.join(',')
    if (f.statuses.length) params.status = f.statuses.join(',')
    if (f.q) params.q = f.q

    try {
      const r = await axios.get(`${API}/api/admin/audit`, {
        headers: HEADERS,
        params,
        signal: c.signal,
      })
      if (inflightRef.current !== c) return
      setData(r.data)
      setError(null)
      setErrorTitle(null)
    } catch (e) {
      if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
      const isKey = e.response?.status === 403
      setError(isKey ? 'Admin data is unavailable in this build.' : (e.message || 'Failed to load'))
      setErrorTitle(isKey ? 'REACT_APP_ADMIN_KEY' : null)
    } finally {
      if (inflightRef.current === c) {
        setLoading(false)
        inflightRef.current = null
      }
    }
  }, [fingerprint])

  useEffect(() => {
    fetchOnce()
    return () => {
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [fetchOnce])

  return { data, loading, error, errorTitle, refetch: fetchOnce }
}
